import { Component, OnInit, OnDestroy, ChangeDetectorRef, ChangeDetectionStrategy } from '@angular/core';
import { CurrencyPipe } from '@angular/common';
import { PaginatedResultModel } from '@app/data-layer/models/common';
import { BillItemModel, BillModel, BillSearchModel, PaymentMethodModel } from '@app/data-layer/models/payments';
import { BillsRepository } from '@app/data-layer/repositories';
import { TranslateService } from '@ngx-translate/core';
import { CompaniesService } from '@services/companies.service';
import { GenericService } from '@services/generic.service';
import { PaginationDynamicWithExportComponent } from '@shared/pagination/components';
import { CustomDateTimeByUtcPipe } from '@shared/pipes-shared/pipes';
import { Observable, ReplaySubject, combineLatest, forkJoin, of } from 'rxjs';
import { takeUntil, map, take, debounceTime, switchMap } from 'rxjs/operators';
import { enumCompanyPaymentType } from '@app/data-layer/enums/companies';
import * as XLSX from 'xlsx';
import { enumPaymentState } from '@app/data-layer/enums/payments';
import { CompanyModel } from '@app/data-layer/models/companies';
import { OrganizationsService } from '@services/organizations.service';
import { OrganizationModel } from '@app/data-layer/models/organizations';
import { Store } from '@ngxs/store';
import {
  FetchBillList,
  OffsetChange,
  ResetBillListData,
  SetDefaultPaginationState,
  UpdateLoadingState,
  UserBillingSelectors,
} from '@app/modules/company/state/billing';
import { DocumentsService } from '@services/documents.service';

@Component({
  selector: 'app-billing-history',
  templateUrl: './billing-history.component.html',
  styleUrls: ['./billing-history.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BillingHistoryComponent extends PaginationDynamicWithExportComponent<BillModel, BillSearchModel> implements OnInit, OnDestroy {
  public organization: OrganizationModel;
  public companies: CompanyModel[];
  public monthlyBillList: PaginatedResultModel<BillModel> = { total: 0, items: [], options: null };
  public paginationMaxSize$: Observable<number>;
  public totalValue: number = 0;
  public paymentMethods: PaymentMethodModel[] = [];
  public defaultSearchStates: enumPaymentState[] = [
    enumPaymentState.SUCCESS,
    enumPaymentState.REFUNDED,
    enumPaymentState.PENDING,
    enumPaymentState.PARTIALLY_REFUNDED,
    enumPaymentState.DELAYED,
  ];

  protected fileNameLabel: string = 'Bills';
  protected batchReferenceNumber: string;
  protected documentIds: number[];

  private destroy$: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private store: Store,
    private billsRepository: BillsRepository,
    private organizationsService: OrganizationsService,
    private companiesService: CompaniesService,
    protected translateService: TranslateService,
    protected documentsService: DocumentsService,
    protected customDateTime: CustomDateTimeByUtcPipe,
    private genericService: GenericService,
    private currency: CurrencyPipe,
    private cdr: ChangeDetectorRef,
  ) {
    super();
  }

  public ngOnInit(): void {
    this.organization = this.organizationsService.getOrganization();
    this.paginationMaxSize$ = this.genericService.paginationMobileMaxSize$;

    this.initializeResults();
    this.loadPaymentMethods();
  }

  public ngOnDestroy(): void {
    this.store.dispatch(new SetDefaultPaginationState());
    this.destroy$.next(true);
  }

  public loadMore(): void {
    const limit: number = this.store.selectSnapshot(UserBillingSelectors.getPageLimit);
    const offset: number = this.store.selectSnapshot(UserBillingSelectors.getPageOffset);
    const total: number = this.store.selectSnapshot(UserBillingSelectors.getTotal);
    const isNextPageExists: boolean = offset + limit < total;

    if (isNextPageExists) {
      this.store.dispatch(new OffsetChange(limit + offset));
    }
  }

  protected searchMethod(filters: BillSearchModel, isShowLoader: boolean): Observable<PaginatedResultModel<BillModel>> {
    let search$: Observable<PaginatedResultModel<BillModel>> = of(null);

    const companyId = filters?.companyId;
    const organizationId: number = !!companyId ? null : this.organization?.id;

    this.store.dispatch(new ResetBillListData());

    combineLatest([this.store.select(UserBillingSelectors.getPageOffset), this.store.select(UserBillingSelectors.getPageLimit)])
      .pipe(
        debounceTime(0),
        switchMap(([offset, limit]: [number, number]) => {
          return this.store.dispatch([
            new FetchBillList({
              filters: {
                ...filters,
                companyId,
                organizationId,
                states: this.defaultSearchStates,
                offset,
                limit,
              },
            }),
          ]);
        }),
        takeUntil(this.destroy$),
      )
      .subscribe(() => {
        this.store.dispatch(new UpdateLoadingState(false));
      });

    return search$;
  }

  protected getArchive(documentIds: number[], referenceNumber?: string): Observable<Blob> {
    return of();
  }

  protected getTableSettings(): {
    cols?: XLSX.ColInfo[];
    merges?: XLSX.Range[];
  } {
    return {
      cols: [{ wpx: 45 }, { wpx: 80 }, { wpx: 100 }, { wpx: 300 }, { wpx: 100 }, { wpx: 80 }, { wpx: 160 }, { wpx: 70 }],
      merges: [{ e: { c: 0, r: 0 }, s: { c: 6, r: 0 } }],
    };
  }

  protected getTableRows(list: BillModel[]): Observable<Array<Array<number | string>>> {
    const result: Array<Observable<Array<string | number>>> = [];
    for (let billIndex: number = 0; billIndex < list.length; billIndex++) {
      result.push(this.getTableRow(list[billIndex], billIndex + 1));
      const bill: BillModel = list[billIndex];
      for (let index: number = 0; index < bill.items.length; index++) {
        result.push(this.getBillItemTableRow(bill.items[index], index + 1));
      }
    }
    return forkJoin(result);
  }

  protected getBillItemTableRow(billItem: BillItemModel, index: number): Observable<Array<string | number>> {
    return of([
      '',
      index,
      this.customDateTime.transform(billItem.timestamp),
      [billItem.referenceNumber, billItem.description].join(' - '),
      (billItem.quantity > 1 ? billItem.quantity + ' x ' : '') + this.currency.transform(billItem.price),
    ]);
  }

  protected getTableRow(bill: BillModel, index: number): Observable<string[]> {
    return forkJoin({
      billType: this.translateService.get('LABELS.' + enumCompanyPaymentType[bill.providerType]),
      stateName: this.translateService.get('PAYMENT_STATE.' + enumPaymentState[bill.state]),
    }).pipe(
      map((data) => [
        String(index),
        data.billType,
        this.customDateTime.transform(bill.paidDate),
        bill?.company?.name,
        this.currency.transform(
          bill?.items?.reduce(
            (prevItem: number, currItem: BillItemModel) => currItem.price * (currItem.quantity > 0 ? currItem.quantity : 1) + prevItem,
            0,
          ),
        ),
        data.stateName,
        bill.providerTransactionId,
        bill.invoiceDocumentNumber,
      ]),
    );
  }

  protected getFilters(): Observable<string[]> {
    const filters: Array<{ label: string; value: string }> = [];
    return filters.length
      ? forkJoin(
          filters.map((filter) =>
            forkJoin({
              label: this.translateService.get(filter.label),
              value: this.translateService.get(filter.value),
            }).pipe(map((translatedFilter) => `${translatedFilter.label}-${translatedFilter.value}`)),
          ),
        )
      : of([]);
  }

  protected getTableHeaders(): Observable<string[]> {
    return forkJoin(
      [
        'LABELS.INDEX',
        'LABELS.TYPE',
        'LABELS.TIMESTAMP',
        'LABELS.PAYER_NAME',
        'LABELS.AMOUNT',
        'LABELS.STATUS',
        'LABELS.TRANSACTION_ID',
        'LABELS.INVOICE_NUMBER',
      ].map((label: string) => this.translateService.get(label)),
    );
  }

  private initializeResults(): void {
    this.search({ limit: this.limit, offset: 0 });
    this.companies = this.companiesService.getCompanies();
  }

  private loadPaymentMethods(): void {
    this.billsRepository
      .allPaymentMethods()
      .pipe(take(1))
      .subscribe((paymentMethods: PaymentMethodModel[]) => {
        this.paymentMethods = paymentMethods;
        this.cdr.markForCheck();
      });
  }
}
